import * as React from "react";
import {Divider, Typography} from "@mui/material";
import {FormattedMessage, MessageDescriptor, useIntl} from "react-intl";
import {usePageTitle} from "@/hooks";

type DividerMargin = "none" | "default";

type PageTitleContainerProps = {
    children: React.ReactNode;
    dividerMargin?: DividerMargin;
    inlineControls?: boolean;
};

export const PageTitleContainer = ({
    children,
    dividerMargin = "default",
    inlineControls = false,
}: PageTitleContainerProps) => {
    const extraStyle = inlineControls ? {display: "flex", justifyContent: "space-between"} : {};

    return (
        <React.Fragment>
            <div style={{marginBottom: "0.8em", ...extraStyle}}>{children}</div>
            <Divider style={{marginBottom: dividerMargin === "default" ? "1.5em" : 0}} />
        </React.Fragment>
    );
};

export const FormattedPageTitle = ({desc, values}: {desc: MessageDescriptor; values?: {[s: string]: any}}) => {
    const intl = useIntl();
    const title = intl.formatMessage(desc, values);

    usePageTitle(title);

    return (
        <PageTitleContainer>
            <Typography variant="h6">{title}</Typography>
        </PageTitleContainer>
    );
};

type PageProps = {
    children: React.ReactNode;
    title: MessageDescriptor;
    subTitle?: MessageDescriptor;
    controls?: React.ReactNode;
    style?: React.CSSProperties;
    titleValues?: {[s: string]: any};
    dividerMargin?: DividerMargin;
    inlineControls?: boolean;
};

export const Page = ({
    children,
    title,
    subTitle,
    controls,
    style,
    titleValues,
    dividerMargin,
    inlineControls,
}: PageProps) => {
    const intl = useIntl();
    const titleText = intl.formatMessage(title, titleValues);

    usePageTitle(titleText);

    return (
        <div style={style}>
            <PageTitleContainer dividerMargin={dividerMargin} inlineControls={inlineControls}>
                <div style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                    <div style={{flexGrow: 1}}>
                        <Typography variant="h6">{titleText}</Typography>
                        <Typography variant="subtitle2" color="textSecondary">
                            {subTitle ? <FormattedMessage {...subTitle} /> : null}
                        </Typography>
                    </div>
                </div>
                <div>{controls}</div>
            </PageTitleContainer>
            {children}
        </div>
    );
};
